import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export interface DateValidationResult {
    isValid: boolean;
    error?: string;
}

/**
 * Valida se uma string representa uma data válida
 */
export const isValidDateString = (dateString: string): boolean => {
    if (!dateString || typeof dateString !== 'string') {
        return false;
    }
    
    // Verifica se o formato está correto
    if (!dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return false;
    }
    
    const date = dayjs(dateString, 'YYYY-MM-DD', true); // strict parsing
    
    // Verifica se a data é válida e se o formato original corresponde exatamente
    return date.isValid() && date.format('YYYY-MM-DD') === dateString;
};

/**
 * Valida se a data inicial é anterior ou igual à data final
 */
export const isValidDateRange = (dataInicio: string, dataFim: string): boolean => {
    if (!isValidDateString(dataInicio) || !isValidDateString(dataFim)) {
        return false;
    }
    
    const inicio = dayjs(dataInicio);
    const fim = dayjs(dataFim);
    
    return inicio.isSameOrBefore(fim);
};

/**
 * Valida se as datas estão dentro de limites aceitáveis
 */
export const isWithinDateLimits = (dateString: string): boolean => {
    if (!isValidDateString(dateString)) {
        return false;
    }
    
    const date = dayjs(dateString);
    const hoje = dayjs().endOf('day'); // Permite até o final do dia atual
    const limitePassado = dayjs().subtract(20, 'years'); // Máximo 20 anos no passado (mais flexível)
    
    return date.isSameOrBefore(hoje) && date.isSameOrAfter(limitePassado);
};

/**
 * Validação básica para garantir que as datas são obrigatórias e têm formato válido
 */
export const validateBasicDateFilters = (dataInicio?: string, dataFim?: string): DateValidationResult => {
    // Verifica se as datas foram fornecidas
    if (!dataInicio || !dataFim) {
        return {
            isValid: false,
            error: 'Data inicial e data final são obrigatórias'
        };
    }
    
    // Verifica se as datas têm formato válido
    if (!isValidDateString(dataInicio)) {
        return {
            isValid: false,
            error: 'Data inicial tem formato inválido.'
        };
    }
    
    if (!isValidDateString(dataFim)) {
        return {
            isValid: false,
            error: 'Data final tem formato inválido.'
        };
    }
    
    // Verifica se o intervalo de datas é válido
    if (!isValidDateRange(dataInicio, dataFim)) {
        return {
            isValid: false,
            error: 'Data inicial deve ser anterior ou igual à data final'
        };
    }
    
    return {
        isValid: true
    };
};

/**
 * Validação completa para DRF (com limites mais restritivos)
 */
export const validateDateFilters = (dataInicio?: string, dataFim?: string): DateValidationResult => {
    // Primeiro faz a validação básica
    const basicValidation = validateBasicDateFilters(dataInicio, dataFim);
    if (!basicValidation.isValid) {
        return basicValidation;
    }
    
    // Verifica se as datas estão dentro dos limites aceitáveis
    if (!isWithinDateLimits(dataInicio!)) {
        return {
            isValid: false,
            error: 'Data inicial está fora dos limites permitidos (máximo 20 anos no passado, não pode ser futura)'
        };
    }
    
    if (!isWithinDateLimits(dataFim!)) {
        return {
            isValid: false,
            error: 'Data final está fora dos limites permitidos (máximo 20 anos no passado, não pode ser futura)'
        };
    }
    
    // Verifica se o intervalo não é muito longo (máximo 5 anos para dados fiscais)
    const inicio = dayjs(dataInicio!);
    const fim = dayjs(dataFim!);
    const diffInMonths = fim.diff(inicio, 'months');
    
    if (diffInMonths > 60) {
        return {
            isValid: false,
            error: 'O período selecionado não pode ser superior a 5 anos'
        };
    }
    
    return {
        isValid: true
    };
};
